import { useCallback, useEffect, useState } from 'react';
import type {
  ProductAnalysis,
  ProductAnalysisRun,
  ProductAnalysisRunState,
  StartProductAnalysisResponse,
} from '@ozon-ai/analysis/contracts';
import { Button } from '@/components/ui/button.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Label } from '@/components/ui/label.tsx';
import { api } from '../../../api/client';
import { PeriodPicker } from '../../../components/period-picker/period-picker';
import { usePeriodPicker } from '../../../components/period-picker/use-period-picker';
import { usePeriodPresets } from '../../../components/period-picker/use-period-presets';
import { useTelegram } from '../../../context/TelegramContext';
import { ProductAnalysisResultView } from '../../../components/product-analysis/ProductAnalysisResultView';
import { ProductAnalysisRunProgressView } from '../../../components/product-analysis/ProductAnalysisRunProgressView';
import { isProductAnalysisRunState } from '../../../components/product-analysis/productAnalysisRunState';
import {
  formatAnalysisDate,
  runStatusLabel,
} from '../../../components/product-analysis/productAnalysisLabels';
import { SETTINGS_ANALYSIS_HUB } from '../settings-paths';
import {
  SettingsErrorAlert,
  SettingsFormCard,
  SettingsHint,
  SettingsSubPageShell,
} from '../settings-shared';

export function SettingsProductAnalysisPage() {
  const { webApp } = useTelegram();
  const { presets, primaryPresetId, loading: presetsLoading } = usePeriodPresets();
  const period = usePeriodPicker({
    presets,
    primaryPresetId,
    presetsReady: !presetsLoading,
  });
  const [offerId, setOfferId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ProductAnalysis | null>(null);
  const [runState, setRunState] = useState<ProductAnalysisRunState | null>(null);
  const [history, setHistory] = useState<ProductAnalysisRun[]>([]);
  const [historyBusy, setHistoryBusy] = useState(false);

  const loadHistory = useCallback(async (id: string) => {
    if (!id.trim()) {
      setHistory([]);
      return;
    }
    setHistoryBusy(true);
    try {
      const rows = await api.get<ProductAnalysisRun[]>(
        `/product-analyses?offerId=${encodeURIComponent(id.trim())}&limit=10`,
      );
      setHistory(rows);
    } catch {
      setHistory([]);
    } finally {
      setHistoryBusy(false);
    }
  }, []);

  useEffect(() => {
    if (offerId.trim()) {
      void loadHistory(offerId);
    }
  }, [offerId, loadHistory]);

  async function handleRun() {
    if (!period.selection) {
      setError(period.localError ?? 'Укажите период');
      return;
    }
    setBusy(true);
    setError(null);
    setResult(null);
    setRunState(null);
    try {
      const started = await api.post<StartProductAnalysisResponse>('/product-analyses/run', {
        offerId: offerId.trim(),
        period: period.selection,
      });

      let finished = false;
      while (!finished) {
        const data = await api.get<ProductAnalysis | ProductAnalysisRunState>(
          `/product-analyses?id=${encodeURIComponent(started.runId)}`,
        );
        if (isProductAnalysisRunState(data)) {
          setRunState(data);
          if (data.status === 'failed') {
            throw new Error(data.error ?? 'Анализ завершился с ошибкой');
          }
          if (data.status === 'running' || data.status === 'created') {
            await new Promise((resolve) => setTimeout(resolve, 1000));
            continue;
          }
        } else {
          setRunState(null);
          setResult(data);
          finished = true;
        }
      }

      await loadHistory(offerId);
      webApp?.HapticFeedback?.notificationOccurred('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось запустить анализ');
      webApp?.HapticFeedback?.notificationOccurred('error');
    } finally {
      setBusy(false);
    }
  }

  async function loadRun(id: string) {
    setBusy(true);
    setError(null);
    try {
      const data = await api.get<ProductAnalysis>(`/product-analyses?id=${encodeURIComponent(id)}`);
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить анализ');
    } finally {
      setBusy(false);
    }
  }

  return (
    <SettingsSubPageShell title="Анализ товара" backTo={SETTINGS_ANALYSIS_HUB}>
      <SettingsHint>
        Анализ одного SKU за период (FBO). Это не дайджест на странице «Планы».
      </SettingsHint>

      <SettingsFormCard>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label>Артикул (offerId)</Label>
            <Input
              value={offerId}
              placeholder="MY-SKU-001"
              onChange={(event) => setOfferId(event.target.value)}
            />
          </div>
          <PeriodPicker {...period} dateFieldIdPrefix="settings-analysis" />
          <Button type="button" disabled={busy || !offerId.trim() || !period.selection} onClick={() => void handleRun()}>
            {busy ? 'Запуск…' : 'Запустить'}
          </Button>
        </div>
      </SettingsFormCard>

      {history.length > 0 && (
        <SettingsFormCard title="История запусков">
          {historyBusy && <SettingsHint>Загрузка…</SettingsHint>}
          <div className="flex flex-col gap-2">
            {history.map((run) => (
              <Button
                key={run.id}
                type="button"
                variant="outline"
                size="sm"
                className="h-auto justify-start whitespace-normal py-2 text-left"
                disabled={busy}
                onClick={() => void loadRun(run.id)}
              >
                {formatAnalysisDate(run.finishedAt ?? run.createdAt)} · {run.periodFrom}–{run.periodTo} ·{' '}
                {runStatusLabel(run.status)}
              </Button>
            ))}
          </div>
        </SettingsFormCard>
      )}

      {error && <SettingsErrorAlert>{error}</SettingsErrorAlert>}

      {runState && (runState.status === 'running' || runState.status === 'created') && (
        <SettingsFormCard>
          <ProductAnalysisRunProgressView progress={runState.progress} />
        </SettingsFormCard>
      )}

      {result && (
        <SettingsFormCard>
          <ProductAnalysisResultView result={result} />
        </SettingsFormCard>
      )}
    </SettingsSubPageShell>
  );
}
